import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  TruckIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { fetchDrivingDriverByUser } from "../../../api/driving/driver";
import { CountUp } from "../../../components/Utils";
import { driverVehicleAction } from "./drivers/utils";
import VehiclesListDialog from "../vehicles/list-dialog";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { axiosWithToast } from "../../../components/Utils";
import { ConfirmDialog } from "../../../components/utils/Prompt";

export default function CurrentVehicleSection(props) {
  const { t } = useTranslation();

  const currentUser = useSelector((state) => state.session.currentUser);
  const currentCompany = useSelector((state) => state.session.currentCompany);

  const [driver, setDriver] = useState({});
  const [vehicle, setVehicle] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [vehiclesListDialogOpen, setVehiclesListDialogOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [checkoutConfirmOpen, setCheckoutConfirmOpen] = useState(false);

  /** Only limit to Automile-linked vehicles when integration is fully configured (same as API check-in). */
  const automileCheckinFilter = Boolean(currentCompany?.automile_ready);

  const checkInVehicleFilters = useMemo(() => {
    const f = [
      { name: "active", value: true },
      { name: "available_for_driver_checkin", value: true },
    ];
    if (automileCheckinFilter) {
      f.push({ name: "synced_with_automile", value: true });
    }
    return f;
  }, [automileCheckinFilter]);

  useEffect(() => {
    const payload = {
      user_id: currentUser?.id,
      fields:
        "id,displayTitle,checked_into_vehicle.id,checked_into_vehicle.number_plate,checked_into_vehicle.distance_driven_today,checked_into_vehicle.distance_driven_this_week,checked_into_vehicle.distance_driven_this_month",
    };
    async function fetchDriver() {
      setIsLoading(true);
      await fetchDrivingDriverByUser(payload)
        .then((response) => {
          if (response?.data?.id) {
            setIsLoading(false);
            setDriver(response?.data);
            if (response?.data?.checked_into_vehicle) {
              setVehicle(response?.data?.checked_into_vehicle);
            }
          } else {
            setIsLoading(false);
            setDriver({});
            setVehicle({});
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error(error);
        });
    }
    if (currentUser) {
      fetchDriver();
    }
  }, [currentUser]);

  const runCheckout = async () => {
    if (!driver?.id) return;
    setIsLoading(true);
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: currentUser?.id,
          vehicleId: vehicle?.id,
          action: "checkout",
        }),
        null,
        null,
        (response) => {
          if (response?.id) {
            setDriver(response);
            setVehicle({});
          }
        }
      );
    } finally {
      setIsLoading(false);
      setCheckoutConfirmOpen(false);
    }
  };

  const handleVehicleCheckin = async (vehicleId) => {
    if (driver?.id) {
      setIsLoading(true);
      try {
        await axiosWithToast(
          driverVehicleAction({
            userId: currentUser?.id,
            vehicleId: vehicleId,
            action: "checkin",
          }),
          null,
          null,
          (response) => {
            if (response?.id) {
              setDriver(response);
              setVehicle(response?.checked_into_vehicle);
            }
          }
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div
        className="flex items-center justify-between p-4 min-h-[56px] cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? t("Collapse") : t("Expand")}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2 rounded-lg ${vehicle?.id ? "bg-blue-50" : "bg-gray-50"}`}>
            <TruckIcon className={`h-6 w-6 ${vehicle?.id ? "text-blue-600" : "text-gray-400"}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {vehicle?.id
                ? vehicle?.number_plate
                : driver?.id
                ? t("Ingen bil")
                : t("Ikke sjåfør")}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {vehicle?.id
                ? `${t("Driven today")}: ${vehicle?.distance_driven_today || 0} km`
                : driver?.id
                ? t("No vehicle checked in")
                : t("Not a driver")}
            </p>
          </div>
        </div>
        <div className="p-2 rounded-lg">
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">{t("Vehicle Actions")}</h4>

            <div className="space-y-3">
              {vehicle?.id ? (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setCheckoutConfirmOpen(true)}
                    type="button"
                    disabled={isLoading}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 transition-all duration-200 hover:shadow-sm disabled:opacity-50"
                  >
                    <div className="p-2 bg-red-50 rounded-lg">
                      <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{t("Check out")}</div>
                      <div className="text-sm text-gray-500">{t("Check out of vehicle")}</div>
                    </div>
                  </button>

                  <Link
                    to={"/dashboard/driving/vehicles/" + vehicle?.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 transition-all duration-200 hover:shadow-sm"
                  >
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Cog6ToothIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{t("Overview")}</div>
                      <div className="text-sm text-gray-500">{t("View vehicle details")}</div>
                    </div>
                  </Link>

                  <Link
                    to={"/dashboard/driving/vehicles/" + vehicle?.id + "/handbook"}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 transition-all duration-200 hover:shadow-sm"
                  >
                    <div className="p-2 bg-green-50 rounded-lg">
                      <BookOpenIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{t("Driver handbook")}</div>
                      <div className="text-sm text-gray-500">
                        {t("Guidelines and instructions for this vehicle")}
                      </div>
                    </div>
                  </Link>
                </div>
              ) : (
                driver?.id && (
                  <button
                    onClick={() => setVehiclesListDialogOpen(true)}
                    type="button"
                    disabled={isLoading}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-gray-50 border border-gray-200 transition-all duration-200 hover:shadow-sm disabled:opacity-50"
                  >
                    <div className="p-2 bg-green-50 rounded-lg">
                      <ArrowLeftOnRectangleIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{t("Check in")}</div>
                      <div className="text-sm text-gray-500">{t("Check into a vehicle")}</div>
                    </div>
                  </button>
                )
              )}
            </div>

            {currentCompany?.automile_client_id && !currentCompany?.automile_ready && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2">{t("automile.settingsHint")}</p>
            )}

            {vehicle?.id && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t("Driving Statistics")}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-500">{t("Today")}</div>
                    <div className="text-lg font-bold text-blue-600">
                      <CountUp value={vehicle?.distance_driven_today || 0} /> km
                    </div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-500">{t("This week")}</div>
                    <div className="text-lg font-bold text-green-600">
                      <CountUp value={vehicle?.distance_driven_this_week || 0} /> km
                    </div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-500">{t("This month")}</div>
                    <div className="text-lg font-bold text-purple-600">
                      <CountUp value={vehicle?.distance_driven_this_month || 0} /> km
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={checkoutConfirmOpen}
        onClose={() => setCheckoutConfirmOpen(false)}
        onConfirm={runCheckout}
        title={t("automile.checkoutConfirmTitle")}
        message={t("automile.checkoutConfirmMessage")}
        confirmText={t("automile.checkoutConfirm")}
        cancelText={t("automile.checkoutCancel")}
      />

      <VehiclesListDialog
        open={vehiclesListDialogOpen}
        onClose={() => setVehiclesListDialogOpen(false)}
        checkInMode
        filters={checkInVehicleFilters}
        onSelect={(vehicleId) => {
          toast.success(t("Vehicle selected"));
          setVehiclesListDialogOpen(false);
          handleVehicleCheckin(vehicleId);
        }}
      />
    </div>
  );
}
